import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DataSeries } from '@next/core-lib/models';
import { NotificationActions } from '@next/core-lib/notification';
import { SiteState } from '@next/core-lib/site';
// @ts-ignore Ignore unused commented imports
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { RouterNavigationAction, ROUTER_NAVIGATED } from '@ngrx/router-store';
// @ts-ignore Ignore unused commented ActionTypes import
import { Action, Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
} from 'rxjs/operators';

import { FlockRegistrationService } from 'src/app/cycle/services/flock-registration.service';
import { ReferenceValueSheetService } from 'src/app/reference-value-sheet/services/reference-value-sheet.service';

import * as actions from './../actions/within-cycle-performance.actions';
import * as cycleActions from '../../../cycle/store/actions/cycle.actions';

import { ObservationService } from '../../services/observation.service';

@Injectable()
export class WithinCyclePerformanceEffects {
  protected readonly actions$ = inject(Actions);
  protected readonly store = inject<Store<SiteState>>(Store);
  private readonly refValSheetService = inject(ReferenceValueSheetService);
  private readonly flockRegistrationService = inject(FlockRegistrationService);
  private readonly observationService = inject(ObservationService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  routerNavigated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      filter(
        (action: RouterNavigationAction) =>
          action.payload.routerState.url.indexOf('/within-cycle-performance') > -1,
      ),
      switchMap(() => of(cycleActions.loadData())),
    ),
  );

  loadRefValSheetsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadRefValSheetDataSeriesRequest),
      switchMap((action) =>
        this.refValSheetService.getReferenceValueSheetDataSeriesById(action.id).pipe(
          switchMap((dataSeries: DataSeries<number, number>) =>
            of(actions.loadRefValSheetDataSeriesSuccess({ refValSheetDataSeries: dataSeries })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadRefValSheetDataSeriesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadWdoDataSeriesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadWdoDataSeriesRequest),
      mergeMap(({ cycleId, barnIds }) =>
        forkJoin(
          barnIds.map((barnId) =>
            this.flockRegistrationService.getCycleWdoDataSeriesByCycleIdAndBarnId(cycleId, barnId),
          ),
        ).pipe(
          switchMap((dataSeries: DataSeries<number, number>[]) =>
            of(actions.loadWdoDataSeriesSuccess({ wdoDataSeries: dataSeries })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadWdoDataSeriesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadGrowthPredictionDataSeriesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadGrowthPredictionDataSeriesRequest),
      mergeMap(({ cycleId, barnIds }) =>
        forkJoin(
          barnIds.map((barnId) =>
            this.flockRegistrationService.getCycleGrowthPredictionDataSeriesByCycleIdAndBarnId(
              cycleId,
              barnId,
            ),
          ),
        ).pipe(
          switchMap((dataSeries: DataSeries<number, number>[]) =>
            of(
              actions.loadGrowthPredictionDataSeriesSuccess({
                growthPredictionDataSeries: dataSeries,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadGrowthPredictionDataSeriesError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadObservationsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadObservationsForCycleRequest),
      debounceTime(100),
      mergeMap(({ cycleId, unitOfMeasureSettings }) =>
        this.observationService.getObservations(cycleId, unitOfMeasureSettings).pipe(
          map((observations) => actions.loadObservationsForCycleSuccess({ observations })),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadObservationsForCycleFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertObservationsRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.upsertObservationsForCycleRequest),
      mergeMap(({ observations, unitOfMeasureSettings }) =>
        forkJoin(
          observations.map((o) =>
            this.observationService.upsertObservation(o, unitOfMeasureSettings),
          ),
        ).pipe(
          map((_observationResponses) =>
            actions.upsertObservationsForCycleSuccess({ observations }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadObservationsForCycleFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
